import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for pull-to-refresh functionality
 * @param {Object} options - Configuration options
 * @param {Function} options.onRefresh - Callback function to execute on refresh (should return a Promise)
 * @param {number} options.threshold - Pull distance threshold to trigger refresh (default: 80)
 * @param {number} options.maxPull - Maximum pull distance (default: 120)
 * @param {boolean} options.disabled - Whether pull-to-refresh is disabled (default: false)
 * @returns {Object} - { isPulling, pullDistance, isRefreshing, containerRef }
 */
export function usePullToRefresh({
  onRefresh,
  threshold = 80,
  maxPull = 120,
  disabled = false
} = {}) {
  const [isPulling, setIsPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const containerRef = useRef(null);
  const startY = useRef(0);
  const currentY = useRef(0);
  const isAtTop = useRef(true);
  
  // Check if we're at the top of the scroll container
  const checkIfAtTop = useCallback(() => {
    if (containerRef.current) {
      isAtTop.current = containerRef.current.scrollTop <= 0;
    } else {
      isAtTop.current = window.scrollY <= 0;
    }
    return isAtTop.current;
  }, []);

  // Handle touch start
  const handleTouchStart = useCallback((e) => {
    if (disabled || isRefreshing) return;
    
    checkIfAtTop();
    if (!isAtTop.current) return;
    
    startY.current = e.touches[0].clientY;
    currentY.current = startY.current;
  }, [disabled, isRefreshing, checkIfAtTop]);

  // Handle touch move
  const handleTouchMove = useCallback((e) => {
    if (disabled || isRefreshing) return;
    if (!isAtTop.current || startY.current === 0) return;
    
    currentY.current = e.touches[0].clientY;
    const diff = currentY.current - startY.current;
    
    // Only activate when pulling down
    if (diff > 0) {
      // Apply resistance to the pull
      const resistance = 0.5;
      const distance = Math.min(diff * resistance, maxPull);
      
      setIsPulling(true);
      setPullDistance(distance);
      
      // Prevent default scroll when pulling
      if (distance > 10) {
        e.preventDefault();
      }
    }
  }, [disabled, isRefreshing, maxPull]);

  // Handle touch end
  const handleTouchEnd = useCallback(async () => {
    if (disabled || isRefreshing) return;
    
    const distance = pullDistance;
    
    // Reset pull state
    setIsPulling(false);
    startY.current = 0;
    currentY.current = 0;
    
    // Check if threshold was reached
    if (distance >= threshold && onRefresh) {
      setIsRefreshing(true);
      setPullDistance(threshold); // Keep indicator visible during refresh
      
      // Provide haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(10);
      }
      
      try {
        await onRefresh();
      } catch (error) {
        console.error('Pull to refresh error:', error);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      // Animate back to 0
      setPullDistance(0);
    }
  }, [disabled, isRefreshing, pullDistance, threshold, onRefresh]);

  // Handle scroll to track if we're at top
  const handleScroll = useCallback(() => {
    checkIfAtTop();
  }, [checkIfAtTop]);

  // Set up event listeners
  useEffect(() => {
    const container = containerRef.current || document;
    const scrollTarget = containerRef.current || window;
    
    // Use passive: false for touchmove to allow preventDefault
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: false });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });
    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      scrollTarget.removeEventListener('scroll', handleScroll);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleScroll]);

  return {
    isPulling,
    pullDistance,
    isRefreshing,
    containerRef,
    progress: Math.min(pullDistance / threshold, 1)
  };
}

export default usePullToRefresh;
